import { Action, ActionCreator, createAction, createActionGroup, emptyProps, props } from '@ngrx/store';

import { ContributorAssignmentAndEntry } from '../../../model/product/contributors';
import { TrackMetadata } from '../../../model/product/track-metadata';
import {
  DmbWizardValue,
  UploadErrorType,
  WizardFeatureState,
  WizardStep,
  initialState,
} from '../../../model/product/wizard';
import { WizardTemplateValues } from '../template/template.model';

// Actions can be accessed e.g. "WizardActions.eanGenerated"
export const WizardActions = createActionGroup({
  source: 'Wizard',
  events: {
    eanGenerated: props<{ ean: string }>(),
    appliedContributorsToTracks: props<{ trackIds: string[] }>(),
    copiedTrackContributors: props<{ sourceTrackId: string | number; targetTrackId: string }>(),
    productContributorsUpdated: props<{
      newAssignments: ContributorAssignmentAndEntry[];
    }>(),
    trackContributorsUpdated: props<{
      id: string;
      newAssignments: ContributorAssignmentAndEntry[];
    }>(),
    artistIdsSelected: props<{
      id: string;
      artistId: string | undefined;
      store: 'Spotify' | 'Apple';
    }>(),
    appliedMetadataToTracks: props<{ trackIds: string[] }>(),
    copiedTrackMetadata: props<{ sourceTrackId: string | number; targetTrackId: string }>(),
    trackMetadataUpdated: props<{ id: string; newMetadata: Partial<TrackMetadata> }>(),
    trackDeleteStarted: props<{ id: string | number }>(),
    trackDeleteDone: props<{ id: string | number }>(),
    countriesToggled: props<{ toggleList: string[] }>(),
    shopsChanged: props<{
      newShops: {
        id: number;
        preselect: boolean;
        downloadAllowed: boolean;
        streamingAllowed: boolean;
        ringtoneAllowed: boolean;
        specialAllowed: boolean;
        specialType: string | undefined;
      }[];
    }>(),
    shopEntryToggled: props<{ id: number; entry: 'download' | 'streaming' | 'ringtone' | 'special' }>(),
    shopRowToggled: props<{ id: number }>(),
    shopColumnToggled: props<{ entry: 'download' | 'streaming' | 'ringtone' | 'special' }>(),
    shopAllToggled: emptyProps(),
    neighbouringRightsCountriesToggled: props<{ toggleList: string[] }>(),
    neighbouringRightsPercentagesUpdated: props<{
      newPercentage: string;
      updatedId: undefined | string; // undefined for all
    }>(),
    neighbouringRightsStartDateUpdated: props<{
      newDate: Date | undefined;
      updatedId: undefined | string;
    }>(),
    neighbouringRightsEndDateUpdated: props<{
      newDate: Date | undefined;
      updatedId: undefined | string;
    }>(),
    templateApplied: props<{ patch: WizardTemplateValues }>(),
  },
});

export const WizardSessionActions = createActionGroup({
  source: 'Wizard Session',
  events: {
    newTimestamp: emptyProps(),
    sessionSelected: props<{ id: string }>(),
    sessionLoaded: props<{ value: DmbWizardValue }>(),
    sessionLoadFailed: props<{ reason: unknown }>(),
    newSession: emptyProps(),
    deleteSessionStarted: props<{ id: string; fileIds: string[] }>(),
    deleteSessionDone: props<{ id: string }>(),
    startedLoadingConstraints: emptyProps(),
    failedLoadingConstraints: emptyProps(),
    canceledLoadingConstraints: emptyProps(),
    finishedLoadingConstraints: props<Omit<WizardFeatureState['constraints'], 'loading' | 'error'>>(),
    stepChanged: props<{ newStep: WizardStep }>(),
    initialLabelSelected: props<{ labelId: string }>(),
    startedLoadingExistingTracks: props<{ ids: number[] }>(),
    finishedLoadingExistingTrack: props<{
      id: number;
      metadata: TrackMetadata;
      contributors: ContributorAssignmentAndEntry[];
      // Passed to update saved values
      title: string | undefined;
      titleVersion: string | undefined;
      isrc: string | undefined;
      displayArtist: string | undefined;
    }>(),
    failedLoadingExistingTrack: props<{ id: number }>(),
    retryLoadingExistingTrack: props<{ id: number }>(),
  },
});

export const AsyncValidationActions = createActionGroup({
  source: 'Async Validation',
  events: {
    startedValidatingProductcode: emptyProps(),
    finishedValidatingProductcode: props<{ result: null | string }>(),
    startedValidatingTrackISRC: props<{ trackId: string }>(),
    finishedValidatingTrackISRC: props<{ trackId: string; error: null | string }>(),
    startedValidatingTitle: props<
      { validating: 'TITLE' | 'TITLE_VERSION' } & (
        | { on: 'ALBUM'; trackId: undefined }
        | { on: 'TRACK'; trackId: string }
      )
    >(),
    finishedValidatingTitle: props<
      {
        errors: null | string[];
        warnings: null | string[];
        explicit: boolean;
        validating: 'TITLE' | 'TITLE_VERSION';
      } & (
        | {
            on: 'ALBUM';
            trackId: undefined;
          }
        | {
            on: 'TRACK';
            trackId: string;
          }
      )
    >(),
    startedValidatingCovers: emptyProps(),
    finishedValidatingCovers: props<{ allErrors: { id: string; errors: null | string[] }[] }>(),
    startedValidatingTrack: props<{ trackId: string }>(),
    finishedValidatingTrack: props<{ trackId: string; errors: null | string[] }>(),
    startedValidatingPreviewFile: emptyProps(),
    finishedValidatingPreviewFile: props<{ errors: null | string[] }>(),
    trackWasAnalyzed: props<{ id: string }>(),
  },
});

export const UploadActions = createActionGroup({
  source: 'Wizard Upload',
  events: {
    start: props<{
      sessionId: string;
      uploads: {
        id: string;
        file: File;
        finishedParts?: { partNumber: number; etag?: string }[];
        uploadId?: string;
        objectUri?: string;
        multipartUrls?: string[];
      }[];
      backoff?: boolean;
      attempt?: number;
    }>(),
    done: props<{ sessionId: string; id: string; objectUri: string }>(),
    multiCreated: props<{ sessionId: string; id: string; uploadId: string; objectUri: string }>(),
    partDone: props<{ sessionId: string; id: string; partNumber: number; etag?: string }>(),
    allPartsDone: props<{ sessionId: string; id: string }>(),
    progress: props<{ sessionId: string; id: string; progress: number }>(),
    error: props<{ sessionId: string; id: string; errorType: UploadErrorType }>(),
    cancel: props<{ sessionId: string; id: string; fileUri: string | undefined; uploadId: string | undefined }>(),
  },
});

export type DeepActionCreator<TargetState> = ActionCreator<
  string,
  (args: { value: TargetState }) => { value: TargetState } & Action<string>
> &
  (TargetState extends object
    ? Readonly<{
        [K in keyof TargetState]: TargetState[K] extends object
          ? DeepActionCreator<TargetState[K]>
          : ActionCreator<string, (args: { value: TargetState[K] }) => { value: TargetState[K] } & Action<string>>;
      }>
    : unknown);

function createNestedActions<TargetState>(
  baseString: string,
  initialValue: TargetState,
  typeList: string[],
): DeepActionCreator<TargetState> {
  const newActionCreator = createAction(baseString, props<{ value: TargetState }>());
  typeList.push(newActionCreator.type);

  if (typeof initialValue === 'object' && initialValue !== null) {
    Object.entries(initialValue).forEach(([key, value]) => {
      const nestedAction = createNestedActions(baseString + ' ' + key, value, typeList);
      Object.defineProperty(newActionCreator, key, { value: nestedAction, enumerable: true });
    });
  }

  return newActionCreator as DeepActionCreator<TargetState>;
}

export const setValueActionTypes: string[] = [];
export const setValueActions = createNestedActions('[Wizard set Value]', initialState.value, setValueActionTypes);
